import React, { useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  PayrollReportData,
  PayrollDistribution,
  PayrollTopPaid,
  PayrollDetailRow,
} from './use-finance-payroll.js';

function fmt(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtPrecise(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function fmtWeek(w: string): string {
  if (!w) return '';
  const d = new Date(w + 'T12:00:00');
  if (isNaN(d.getTime())) return w;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function DistributionRow({ item, totalPayroll }: { item: PayrollDistribution; totalPayroll: number }) {
  const pct = totalPayroll > 0 ? (item.amount / totalPayroll) * 100 : 0;
  return (
    <div style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{item.label}</div>
        <div style={{ fontSize: 12, color: C.textMuted }}>{item.headcount} employee{item.headcount !== 1 ? 's' : ''}</div>
        <div style={{ height: 4, background: C.borderSoft, borderRadius: 2, marginTop: 6, width: '100%' }}>
          <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: C.gold, borderRadius: 2 }} />
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{fmt(item.amount)}</div>
        <div style={{ fontSize: 11, color: C.textMuted }}>{pct.toFixed(1)}%</div>
      </div>
    </div>
  );
}

function TopPaidRow({ person, rank }: { person: PayrollTopPaid; rank: number }) {
  return (
    <div style={{ padding: '8px 12px', borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gridTemplateColumns: '24px 1fr auto', gap: 10, alignItems: 'center' }}>
      <div style={{ fontSize: 12, color: C.textMuted, textAlign: 'center' }}>{rank}</div>
      <div>
        <div style={{ fontSize: 13, color: C.text }}>{person.name}</div>
        <div style={{ fontSize: 11, color: C.textMuted }}>
          {person.driver_id ? `ID: ${person.driver_id}` : ''}
          {person.terminal ? ` \u00b7 ${person.terminal}` : ''}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.gold }}>{fmtPrecise(person.total_pay)}</div>
    </div>
  );
}

function CategoryDetailSection({ category, label, rows }: { category: string; label: string; rows: PayrollDetailRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const catTotal = rows.reduce((s, r) => s + r.total_pay, 0);

  return (
    <div style={{ borderRadius: 10, border: `1px solid ${C.borderSoft}`, overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', padding: '12px 16px', background: C.panel, border: 'none',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', color: C.text, fontSize: 13, fontWeight: 600,
        }}
      >
        <span>{label} ({rows.length})</span>
        <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ color: C.gold }}>{fmt(catTotal)}</span>
          <span style={{ color: C.textMuted, fontSize: 12 }}>{expanded ? '\u25b2' : '\u25bc'}</span>
        </span>
      </button>
      {expanded && (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: C.surface }}>
                {['Name', 'ID', 'Terminal', 'Rate', 'Days', 'Base', 'Bonus', 'Total'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: C.textMuted, fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${C.borderSoft}` }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.sort((a, b) => b.total_pay - a.total_pay).map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                  <td style={{ padding: '8px 10px', color: C.text }}>{row.name}</td>
                  <td style={{ padding: '8px 10px', color: C.textMuted }}>{row.driver_id || '\u2014'}</td>
                  <td style={{ padding: '8px 10px', color: C.textMuted }}>{row.terminal || '\u2014'}</td>
                  <td style={{ padding: '8px 10px', color: C.textMuted }}>
                    ${row.rate.toFixed(2)}{row.rate_type ? `/${row.rate_type}` : ''}
                  </td>
                  <td style={{ padding: '8px 10px', color: C.textMuted }}>{row.days || '\u2014'}</td>
                  <td style={{ padding: '8px 10px', color: C.text }}>{fmtPrecise(row.base_pay)}</td>
                  <td style={{ padding: '8px 10px', color: row.bonus > 0 ? C.gold : C.textMuted }}>
                    {row.bonus > 0 ? fmtPrecise(row.bonus) : '\u2014'}
                  </td>
                  <td style={{ padding: '8px 10px', color: C.gold, fontWeight: 600 }}>{fmtPrecise(row.total_pay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  managers: 'Managers',
  drivers_lex: 'Lexington Drivers',
  drivers_evv: 'Evansville Drivers',
  drivers_lburg: 'Lawrenceburg Drivers',
  drivers_marion: 'Marion Drivers',
  drivers_stl: 'STL Drivers',
  contingency_spread: 'Contingency (Spread)',
};

interface FinancePayrollReportProps {
  data: PayrollReportData;
  onRefresh: () => void;
}

export default function FinancePayrollReport({ data, onRefresh }: FinancePayrollReportProps) {
  const { distribution, total_payroll, top_paid, detail_by_category, weekly_trend, period } = data;

  const trendChartData = weekly_trend.map((w) => ({
    week: fmtWeek(w.week_ending),
    Payroll: w.total,
  }));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{fmt(total_payroll)}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Total payroll \u00b7 {period.weeks_included} week{period.weeks_included !== 1 ? 's' : ''}
          </div>
        </div>
        <button
          onClick={onRefresh}
          style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}
        >
          Refresh
        </button>
      </div>

      {/* Distribution by category */}
      {distribution.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Distribution by category
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {distribution.map((item) => (
              <DistributionRow key={item.category} item={item} totalPayroll={total_payroll} />
            ))}
          </div>
        </div>
      )}

      {/* Weekly trend chart */}
      {trendChartData.length > 1 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 12 }}>
            Weekly payroll trend
          </div>
          <div style={{ width: '100%', height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendChartData}>
                <CartesianGrid stroke={C.borderSoft} vertical={false} />
                <XAxis dataKey="week" stroke={C.textMuted} tickLine={false} axisLine={false} fontSize={11} />
                <YAxis stroke={C.textMuted} tickLine={false} axisLine={false} fontSize={11} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 8, fontSize: 12 }}
                  formatter={(value: number) => fmt(value)}
                />
                <Bar dataKey="Payroll" fill={C.gold} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top paid */}
      {top_paid.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Top paid ({top_paid.length})
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {top_paid.map((person, i) => (
              <TopPaidRow key={person.driver_id || i} person={person} rank={i + 1} />
            ))}
          </div>
        </div>
      )}

      {/* Detail by category — collapsible */}
      {Object.keys(detail_by_category).length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Payroll detail
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {Object.entries(detail_by_category).map(([cat, rows]) => (
              <CategoryDetailSection
                key={cat}
                category={cat}
                label={CATEGORY_LABELS[cat] || cat}
                rows={rows}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
